import dayjs from 'dayjs';
import { Box, Card, CardContent, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { setBookAnalysisUI } from '../../../../redux/aum/aum.slice';

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

import GreatDateRangePickerButton from '../../../../components/ui/forms/fields/dateRangePickerButton';
import formatMoney from '../../../../helpers/formatMoneyHelper';
import Loader from '../../../../components/ui/loader';

const ORANGE = 'rgb(203, 100, 35)';

export default function AUMPerformanceChart({
  data,
  loading,
  period,
  setPeriod,
  dateRange,
  setDateRange
}) {
  const dispatch = useDispatch();

  const handlePeriodChange = (_, value) => {
    if (!value || value === period) return;

    setPeriod(value);

    dispatch(
      setBookAnalysisUI({
        period: value
      })
    );
  };

  const chartData = useMemo(() => {
    return [...(data || [])]
      .sort((a, b) => new Date(a.asof) - new Date(b.asof))
      .map((item) => ({
        ...item,
        label: dayjs(item.asof).format('D MMM')
      }));
  }, [data]);

  const HALF_BILLION = 500000000;

  const { yMin, yMax, yTicks } = useMemo(() => {
    if (!chartData.length) {
      return {
        yMin: 0,
        yMax: HALF_BILLION,
        yTicks: [0, HALF_BILLION]
      };
    }

    const values = chartData.map((d) => d.total);

    const max = Math.max(...values);

    // Round up to nearest 500M
    const yMax = Math.ceil(max / HALF_BILLION) * HALF_BILLION;

    const yTicks = [];
    for (let value = 0; value <= yMax; value += HALF_BILLION) {
      yTicks.push(value);
    }

    return {
      yMin: 0,
      yMax,
      yTicks
    };
  }, [chartData]);

  const comparisonLabel = useMemo(() => {
    switch (period) {
      case 'week':
        return 'over the last week';

      case 'month':
        return 'over the last month';

      case 'quarter':
        return 'over the last quarter';

      case 'ytd':
        return 'Year to date';

      case 'custom':
        if (dateRange?.from && dateRange?.to) {
          return `${dayjs(dateRange.from).format('DD MMM YYYY')} - ${dayjs(dateRange.to).format(
            'DD MMM YYYY'
          )}`;
        }

        return 'Selected period';

      default:
        return '';
    }
  }, [period, dateRange]);

  const start = chartData.length ? chartData[0] : null;
  const latest = chartData.length ? chartData.at(-1) : null;

  const percentage =
    start && latest && start.total ? ((latest.total - start.total) / start.total) * 100 : 0;
  return (
    <Box>
      <Typography variant="headingText" fontWeight={700} sx={{ fontSize: '24px' }}>
        AUM Performance
      </Typography>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mb={1}>
        <Box>
          {' '}
          <Box display="flex" alignItems="center" gap={1}>
            <ToggleButtonGroup
              exclusive
              value={period}
              onChange={handlePeriodChange}
              size="small"
              sx={{
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  minWidth: 60,
                  height: 40,
                  border: '1px solid',
                  borderColor: 'divider',
                  textTransform: 'none',
                  fontWeight: 600,

                  '&:not(:first-of-type)': {
                    marginLeft: 0
                  },

                  '&.Mui-selected': {
                    backgroundColor: ORANGE,
                    color: '#fff',
                    borderColor: ORANGE,

                    '&:hover': {
                      backgroundColor: ORANGE
                    }
                  },

                  '&:hover': {
                    backgroundColor: 'rgba(203, 100, 35, 0.08)'
                  }
                }
              }}>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="quarter">Quarter</ToggleButton>
              <ToggleButton value="ytd">YTD</ToggleButton>
            </ToggleButtonGroup>

            <GreatDateRangePickerButton
              selected={period === 'custom'}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
                setPeriod('custom');
              }}
            />
          </Box>
        </Box>
      </Box>
      <Card
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          
          border: '1px solid #ECECEC',
          boxShadow: '0 2px 12px rgba(0,0,0,.05)'
        }}>
        <CardContent sx={{ p: 2 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700
            }}>
            Total Book AUM
          </Typography>

          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: 1.1,
              mt: 1
            }}>
            {latest ? formatMoney(latest.total) : 'R0'}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color: percentage >= 0 ? 'success.main' : 'error.main',
              fontWeight: 600,
              fontSize: 10
            }}>
            {percentage >= 0 ? '▲' : '▼'} {Math.abs(percentage).toFixed(2)}% {comparisonLabel}
          </Typography>

          <Box mt={4} height={320}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Loader loading message="Loading Book Analysis..." />
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{
                    top: 10,
                    right: 15,
                    left: 5,
                    bottom: 5
                  }}>
                  <defs>
                    <linearGradient id="aumGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ORANGE} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={ORANGE} stopOpacity={0.08} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid vertical={false} stroke="#F4F4F4" strokeDasharray="0" />

                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    tickMargin={10}
                    tick={{
                      fill: '#666',
                      fontSize: 12
                    }}
                    tickFormatter={(value, index) => {
                      const date = dayjs(chartData[index]?.asof);

                      switch (period) {
                        case 'week':
                          return value;

                        case 'month':
                          // Every 7th point
                          return index % 7 === 0 ? value : '';

                        case 'quarter':
                        case 'ytd':
                          // First day of each month
                          return date.date() === 1 ? date.format('MMM') : '';

                        case 'custom': {
                          const totalDays =
                            chartData.length > 1
                              ? dayjs(chartData.at(-1).asof).diff(dayjs(chartData[0].asof), 'day')
                              : 0;

                          if (totalDays <= 10) {
                            return value;
                          }

                          if (totalDays <= 45) {
                            return index % 7 === 0 ? value : '';
                          }

                          return date.date() === 1 ? date.format('MMM') : '';
                        }

                        default:
                          return value;
                      }
                    }}
                  />
                  <YAxis
                    domain={[yMin, yMax]}
                    ticks={yTicks}
                    tickFormatter={(v) => `${(v / 1000000000).toFixed(1)}B`}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={12}
                    tick={{
                      fill: '#666',
                      fontSize: 16
                    }}
                  />
                  <Tooltip
                    cursor={false}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;

                      const point = payload[0].payload;

                      return (
                        <Box
                          sx={{
                            bgcolor: '#fff',
                            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                            p: 1.5,
                            border: '1px solid #ECECEC'
                          }}>
                          <Typography fontSize={12} color="text.secondary">
                            {dayjs(point.asof).format('DD MMM YYYY')}
                          </Typography>

                          <Typography fontWeight={700} mt={0.5}>
                            {point.total.toLocaleString('en-ZA', {
                              style: 'currency',
                              currency: 'ZAR',
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Typography>
                        </Box>
                      );
                    }}
                  />

                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke={ORANGE}
                    strokeWidth={0.5}
                    fill="url(#aumGradient)"
                    fillOpacity={1}
                    dot={{
                      r: 1,
                      fill: ORANGE,
                      stroke: ORANGE,
                      strokeWidth: 1
                    }}
                    activeDot={{
                      r: 3,
                      fill: ORANGE,
                      stroke: '#fff',
                      strokeWidth: 2
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
